import './Dashboard.css';
import React, { useState, useRef } from 'react';
import { Grid, Card, Box, Typography, Skeleton, Avatar, TextField, IconButton, Tooltip, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { MyLocation, DeviceThermostat, Thunderstorm, WindPower, Flag, Coronavirus, ArrowBack, LocalHospital, Person } from '@mui/icons-material';
import stringSimilarity from 'string-similarity';
import axios from 'axios';

import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Clock from '../Clock/Clock';

const dummy_user = {
  "id": 0,
  "first_n": "George",
  "last_n": "Wong",
  "dob": "02/11/1987",
  "health_conditions": ["Type 1 Diabetes", "Hayfever"]
}

const health_conditions = [
  { "id": 1, "name": "Type 1 Diabetes" }
]

const pathogens = [
  { "id": 0, "name": "COVID-19", "ages_vuln": [[0, 19], [45, 99]] },
  { "id": 1, "name": "Rhinovirus", "ages_vuln": [[0, 99]] },
  { "id": 2, "name": "Respiratory Synctial Virus", "ages_vuln": [[0 - 9], [75 - 99]] }
]

const baseUrl_dev = "afzpve4n13.execute-api.ap-southeast-2.amazonaws.com";
const baseUrl_prod = "https://ai8nyjlwr6.execute-api.ap-southeast-2.amazonaws.com";
const groupName_alpha = "alpha/H09A_ALPHA";
const groupName_echo = "echo/H09A_ECHO";
const endpoint_weather = "hello";
const endpoint_disease = "daily_diseases"

function Dashboard({ isLoggedIn, mapInstance, onPlaceSelected }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(dummy_user);
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [place, setPlace] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [weatherData, setWeatherData] = useState(null);
  const [diseaseData, setDiseaseData] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  const handleAvatarClick = () => {
    setShowProfile(!showProfile);
  };

  function ProfileSection({ user }) {
    return (
      <Card className="card-container">
        <IconButton onClick={handleAvatarClick}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="div">
          {user.first_n} {user.last_n}
        </Typography>
        <Typography variant="body1">Date of Birth: {user.dob}</Typography>
        <Typography variant="h6" sx={{ marginTop: 2 }}>
          Health Conditions
        </Typography>
        {user.health_conditions.length > 0 ? (
          <List sx={{ marginTop: 1 }}>
            {user.health_conditions.map((condition) => (
              <ListItem key={condition.id}>
                <ListItemAvatar>
                  <Avatar>
                    <LocalHospital />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText sx={{color:"black"}} primary={condition} >{condition}</ListItemText>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body1">None</Typography>
        )}
      </Card>
    );
  }

  const getStateFromLatLng = async (latitude, longitude) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const latLng = new window.google.maps.LatLng(latitude, longitude);
  
      return new Promise((resolve, reject) => {
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK) {
            resolve(results[0]);
          } else {
            reject(new Error("Geocoder failed due to: " + status));
          }
        });
      });
    } catch (err) {
      throw new Error("Failed to load Google Maps API: " + err);
    }
  };

  const retrieveWeatherData = async (latitude, longitude) => {
    const apiKey = "tier2_key1";

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1);

    const startDateString = startOfToday.toISOString().slice(0, 10);
    const endDateString = endOfToday.toISOString().slice(0, 10);

    const startTimeString = '00:00:00.000000';
    const endTimeString = '23:59:59.999999';

    const startDateTimeString = `${startDateString} ${startTimeString}`;
    const endDateTimeString = `${endDateString} ${endTimeString}`;

    const duration = 30;

    try {
      const response = await axios.get(`${groupName_alpha}/${endpoint_weather}?key=${apiKey}&latitude=${latitude}&longitude=${longitude}&start_date=${startDateTimeString}&end_date=${endDateTimeString}&duration=${duration}`);
      setWeatherData(response.data.events[response.data.events.length - 1].attribute);
    } catch (error) {
      console.log(error);
    }
  }

  function getStateFromPlacesObject(placesObject, map) {
    let state = null;

    // loop over each address component
    for (let component of placesObject.address_components) {
      // check if this component is a state
      if (component.types.includes('administrative_area_level_1')) {
        state = component.long_name;
        break;
      }
    }

    // check if the state is in the map
    if (map.has(state)) {
      return map.get(state);
    }

    // find the closest match in the map using string similarity
    const states = Array.from(map.keys());
    const matches = stringSimilarity.findBestMatch(state, states);
    const closestMatch = matches.bestMatch.target;
    return map.get(closestMatch);
  }

  const retrieveDiseaseData = async (location) => {
    try {
      const response = await axios.get(`${groupName_echo}/${endpoint_disease}`);
      const processedData = processDiseaseData(response.data.daily_disease);
      console.log(location);
      console.log(processedData);

      setDiseaseData(getStateFromPlacesObject(location, processedData));

    } catch (error) {
      console.log(error);
    }
  }

  const processDiseaseData = (json_data) => {
    // create a map to store the processed data
    let processed_data = new Map();

    // loop over each item in the JSON data array
    for (let item of json_data) {
      // get the location (state) from the data object
      let location = item.data[0].location;

      // if this is a new location, add it to the map
      if (!processed_data.has(location)) {
        processed_data.set(location, {
          diseases: new Map(),
          reports: 0,
          all_diseases: []
        });
      }

      // loop over each data object in the current item
      for (let data_obj of item.data) {
        // increment the number of reports for this location
        processed_data.get(location).reports++;

        // add the disease name to the list of all diseases for this location
        let disease_name = data_obj.disease_name;
        if (!processed_data.get(location).all_diseases.includes(disease_name)) {
          processed_data.get(location).all_diseases.push(disease_name);
        }

        // increment the counter for this disease name at this location
        let diseases = processed_data.get(location).diseases;
        diseases.set(disease_name, (diseases.get(disease_name) || 0) + 1);
      }
    }

    return processed_data;
  }

  const handlePlaceSelected = async (location) => {
    setLoading(true);
    // Call the onPlaceSelected callback from the App component
    onPlaceSelected(location.geometry.location);
    await retrieveWeatherData(location.geometry.location.lat().toFixed(6), location.geometry.location.lng().toFixed(6));
    await retrieveDiseaseData(location);
    console.log("Disease data");
    console.log(diseaseData);
    setLoading(false);


  };

  useEffect(() => {
    console.log('isLoggedIn:', isLoggedIn);
    if (!isLoggedIn) {
      //navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (mapInstance && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
      autocomplete.bindTo("bounds", mapInstance);

      autocomplete.addListener("place_changed", () => {
        const autoCompletePlace = autocomplete.getPlace();
        setPlace(autoCompletePlace);
        if (!autoCompletePlace.geometry || !autoCompletePlace.geometry.location) {
          window.alert("No details available for input: '" + autoCompletePlace.name + "'");
          return;
        }
        setLocationName(autoCompletePlace.formatted_address);
        handlePlaceSelected(autoCompletePlace);
      });
    }
  }, [mapInstance]);

  if (!isLoggedIn) {
    return null;
  }




  function getInitials() {
    return user["first_n"][0] + user["last_n"][0]
  }

  return (
    <div className="App">
      <Grid container spacing={2} className="dashboard-container">
        <Grid item xs={12} md={4}>

          {showProfile ? (
            <ProfileSection user={user} />
          ) : (
            <Card className="card-container">
              <Avatar onClick={handleAvatarClick} sx={{ marginBottom: 2, bgcolor: "#f8b500", width: 75, height: 75 }}>
                {getInitials()}
              </Avatar>
              <Typography variant="h5" component="div">
                Welcome back, <b>{user["first_n"]}</b>!
              </Typography>
              <Clock />
              <TextField
                inputRef={inputRef}
                label="Search locations"
                variant="outlined"
                margin="normal"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  style: {
                    borderRadius: '3px',
                  },
                  endAdornment: (
                    <IconButton onClick={() => {
                      navigator.geolocation.getCurrentPosition((position) => {
                        getStateFromLatLng(position.coords.latitude, position.coords.longitude)
                        .then((result) => {
                          setPlace(result);
                          handlePlaceSelected(result);
                        })
                        .catch((error) => console.error(error));
                        setLocationName("Current location");
                      });
                    }}>
                      <MyLocation />
                    </IconButton>
                  ),
                }}
              />
              {(place == null) ? <Box></Box> :
                <Box sx={{ maxHeight: "50vh", overflowY: 'scroll' }}>
                  <Typography variant="h6">Showing results for:<br /><b>{locationName}</b></Typography>
                  {(loading || weatherData == null || diseaseData == null)
                    ?
                    <Box sx={{ marginTop: 5 }}>
                      <Skeleton animation="wave" height={50} />
                      <Skeleton animation="wave" height={200} />
                      <Skeleton animation="wave" height={50} />
                      <Skeleton animation="wave" height={200} />
                      <Skeleton animation="wave" height={50} />
                    </Box>
                    :
                    <Box sx={{ marginTop: 5, color: "gray" }}>
                      <Typography variant="h6">WEATHER</Typography>
                      <Grid container sx={{ marginTop: 2, fontWeight: 500 }}>
                        <Grid item xs={4} sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ color: "darkslategrey" }}>{weatherData["Air Temperature"]}°C</Typography>
                          <Card sx={{ margin: 2, backgroundColor: "#f8b500", boxShadow: "none", textAlign: "center", padding: 2 }}>
                            <DeviceThermostat />
                          </Card>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ color: "darkslategrey" }}>{weatherData["Rainfall"]}mm</Typography>
                          <Card sx={{ margin: 2, backgroundColor: "#90a3db", boxShadow: "none", textAlign: "center", padding: 2 }}>
                            <Thunderstorm />
                          </Card>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ color: "darkslategrey" }}>{weatherData["Wind Speed"]}km/h</Typography>
                          <Card sx={{ margin: 2, backgroundColor: "#dde3f4", boxShadow: "none", textAlign: "center", padding: 2 }}>
                            <WindPower />
                          </Card>
                        </Grid>
                      </Grid>
                      <Box sx={{ textAlign: "right", marginRight: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Data collected from {weatherData["Station Name"]}
                        </Typography>
                      </Box>
                      <Typography variant="h6">HEALTH RISKS</Typography>
                      <Grid container sx={{ marginTop: 2, fontWeight: 500, maxHeight: 170 }}>
                        <Grid item xs={6} sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ color: "darkslategrey" }}>{diseaseData["reports"]}</Typography>
                          <Card sx={{ margin: 2, backgroundColor: "#fe7c73", boxShadow: "none", textAlign: "center", padding: 2, color: "black" }}>
                            <Flag />
                            <Typography variant="h5">Virus reports</Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ color: "darkslategrey" }}>{diseaseData["all_diseases"].length}</Typography>
                          <Tooltip title={diseaseData["all_diseases"].join(", ")}>
                            <Card sx={{ margin: 2, backgroundColor: "#f1cbff", boxShadow: "none", textAlign: "center", padding: 2, color: "black" }}>
                              <Coronavirus />
                              <Typography variant="h5">Unique viruses</Typography>
                            </Card>
                          </Tooltip>
                        </Grid>
                      </Grid>

                      <Typography variant="h6">RECOMMENDATIONS</Typography>
                      <Grid container sx={{ marginTop: 2, fontWeight: 500, maxHeight: 170 }}>
                        <Grid item xs={12} sx={{ textAlign: "center" }}>
                          <Box sx={{ margin: 2, boxShadow: "none", textAlign: "center", color: "black", display: 'flex', alignItems: "center" }}>
                            <Avatar sx={{ bgcolor: "#dde3f4", marginRight: 2, width: 50, height: 50 }} variant="rounded">
                              <WindPower sx={{ color: "black" }} />
                            </Avatar>
                            <Typography variant="h6" sx={{textAlign:'left'}}>Pack antihistamines.</Typography>
                            <Typography variant="body2" color="text.secondary">Wind speed is greater than {weatherData["Wind Speed"]-1}km/h.</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: "center" }}>
                          <Box sx={{ margin: 2, boxShadow: "none", textAlign: "center", color: "black", display: 'flex', alignItems: "center" }}>
                            <Avatar sx={{ bgcolor: "#fe7c73", marginRight: 2, width: 50, height: 50 }} variant="rounded">
                              <Coronavirus sx={{ color: "black" }} />
                            </Avatar>
                            <Typography variant="h6" sx={{textAlign:'left'}}>Wear a mask.</Typography>
                            <Typography variant="body2" color="text.secondary">There are multiple virus reports in your area.</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: "center" }}>
                          <Box sx={{ margin: 2, boxShadow: "none", textAlign: "center", color: "black", display: 'flex', alignItems: "center" }}>
                            <Avatar sx={{ bgcolor: "#CDCDCD", marginRight: 2, width: 50, height: 50 }} variant="rounded">
                              <Person sx={{ color: "black" }} />
                            </Avatar>
                            <Typography variant="h6" sx={{textAlign:'left'}}>Bring your insulin pack.</Typography>
                            <Typography variant="body2" color="text.secondary">To ensure healthy blood sugar levels.</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  }
                </Box>
              }
            </Card>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default Dashboard;
